import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { APP_STATUS } from 'src/constants/common'
import {
  buildAndSendMessage,
  getConversation,
  loadMoreMessages as loadMoreMessagesApi,
} from 'src/services/conversations/client'

import { RootState } from '../configureStore'

// INIT STATE
type ConnectionStatus = 'pending' | 'accepted' | 'rejected'
interface Message {
  sid: string
  message?: string
  attributes?: Record<string, string>
  timestamp?: number
  author?: string
  conversationSid?: string
}
interface Connection {
  sid: string
  status: ConnectionStatus
  messages?: Message[]
  hasMore?: boolean
  isLoadingMore?: boolean
}

export interface ConnectionState {
  selectedConnectionSid?: string
  selectedConnection?: Connection
  selectedMembersAvatar?: Record<string, string>
  connections: Connection[]
  setSelectedConnection: {
    status: APP_STATUS
    error?: string
  }
  sendMessage: {
    status: APP_STATUS
    error?: string
  }
  loadMoreMessages: {
    status: APP_STATUS
    error?: string
  }
}

const initialState: ConnectionState = {
  connections: [],
  setSelectedConnection: {
    status: APP_STATUS.INIT,
  },
  sendMessage: {
    status: APP_STATUS.INIT,
  },
  loadMoreMessages: {
    status: APP_STATUS.INIT,
  },
}

// ACTIONS
const setSelectedConnection = createAsyncThunk(
  'connection/setSelectedConnection',
  (sid: Connection['sid']) => getConversation(sid),
  {
    condition: (sid, { getState }) => {
      const conn = (getState() as RootState).connection
      // Already loaded for this sid → skip.
      if (conn.selectedConnection?.sid === sid) return false
      // Already loading for this sid → skip.
      if (
        conn.setSelectedConnection.status === APP_STATUS.LOADING &&
        conn.selectedConnectionSid === sid
      ) {
        return false
      }
      return true
    },
  }
)

const sendMessage = createAsyncThunk(
  'connection/sendMessage',
  async ({ message, userId }: { message: string; userId: string }, { getState }) => {
    const {
      connection: { selectedConnection },
    } = getState() as RootState

    return buildAndSendMessage({
      sid: selectedConnection!.sid,
      message,
      userId,
    })
  }
)

const loadMoreMessages = createAsyncThunk(
  'connection/loadMoreMessages',
  async (_, { getState }) => {
    const {
      connection: { selectedConnection },
    } = getState() as RootState

    if (!selectedConnection) {
      throw new Error('No selected connection')
    }

    return loadMoreMessagesApi(selectedConnection.sid)
  }
)

// REDUCERS
const connectionSlice = createSlice({
  name: 'connection',
  initialState,
  reducers: {
    resetSelectedConnection(state) {
      state.selectedConnection = undefined
      state.selectedMembersAvatar = undefined
      state.selectedConnectionSid = undefined
    },
    resetSelectedConnectionContent(state) {
      state.selectedConnection = undefined
      state.selectedMembersAvatar = undefined
    },
    onReceiveMessage(state, action: PayloadAction<Message>) {
      const message = action.payload
      const selectedConnection = state.selectedConnection

      // If we have a selected connection, update its messages
      if (selectedConnection) {
        state.selectedConnection = {
          ...selectedConnection,
          messages: [...(selectedConnection.messages ?? []), message],
        }
      }

      // Also update the message in the connections list
      const connectionIndex = state.connections.findIndex(
        (conn) => conn.sid === message.conversationSid
      )

      if (connectionIndex !== -1) {
        state.connections[connectionIndex] = {
          ...state.connections[connectionIndex],
          messages: [...(state.connections[connectionIndex].messages ?? []), message],
        }
      }
    },
    setMembersAvatar(state, action: PayloadAction<Record<string, string>>) {
      state.selectedMembersAvatar = action.payload
    },
  },
  extraReducers: (builder) => {
    // SetSelectedConnection
    builder
      .addCase(setSelectedConnection.pending, (state, action) => {
        state.setSelectedConnection.status = APP_STATUS.LOADING
        state.setSelectedConnection.error = undefined
        state.selectedConnectionSid = action.meta.arg
        // Clear stale data when switching to a different conversation so the
        // UI doesn't briefly show the previous conversation's messages.
        if (state.selectedConnection?.sid !== action.meta.arg) {
          state.selectedConnection = undefined
          state.selectedMembersAvatar = undefined
        }
      })
      .addCase(setSelectedConnection.fulfilled, (state, action) => {
        state.selectedConnection = action.payload
        state.setSelectedConnection.status = APP_STATUS.DONE
      })
      .addCase(setSelectedConnection.rejected, (state) => {
        state.setSelectedConnection.status = APP_STATUS.ERROR
        state.setSelectedConnection.error = 'Failed to init selected connection'
      })

    // SendMessage
    builder
      .addCase(sendMessage.pending, (state) => {
        state.sendMessage.status = APP_STATUS.LOADING
        state.sendMessage.error = undefined
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        console.log(action.payload)
        state.sendMessage.status = APP_STATUS.DONE
      })
      .addCase(sendMessage.rejected, (state) => {
        state.sendMessage.status = APP_STATUS.ERROR
        state.sendMessage.error = 'Failed to init selected connection'
      })

    // LoadMoreMessages
    builder
      .addCase(loadMoreMessages.pending, (state) => {
        state.loadMoreMessages.status = APP_STATUS.LOADING
        state.loadMoreMessages.error = undefined
        if (state.selectedConnection) {
          state.selectedConnection.isLoadingMore = true
        }
      })
      .addCase(loadMoreMessages.fulfilled, (state, action) => {
        state.loadMoreMessages.status = APP_STATUS.DONE
        if (state.selectedConnection) {
          // Prepend older messages to the beginning
          const newMessages = action.payload.messages
          const existingMessages = state.selectedConnection.messages ?? []
          state.selectedConnection.messages = [...newMessages, ...existingMessages]
          state.selectedConnection.hasMore = action.payload.hasMore
          state.selectedConnection.isLoadingMore = false
        }
      })
      .addCase(loadMoreMessages.rejected, (state) => {
        state.loadMoreMessages.status = APP_STATUS.ERROR
        state.loadMoreMessages.error = 'Failed to load more messages'
        if (state.selectedConnection) {
          state.selectedConnection.isLoadingMore = false
        }
      })
  },
})

export const {
  resetSelectedConnection,
  resetSelectedConnectionContent,
  onReceiveMessage,
  setMembersAvatar,
} = connectionSlice.actions

export { setSelectedConnection, sendMessage, loadMoreMessages }
export default connectionSlice.reducer
